import { Form } from 'antd';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useAdminUsersQuery, usePurchaseOrderByIdQuery, useUpdatePurchaseOrderManagementMutation } from 'api';

interface UseAssignmentEditProps {
  onCancelModal: () => void;
  open: boolean;
}

export const useAssignmentEdit = (props: UseAssignmentEditProps) => {
  const { onCancelModal, open } = props;
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const { handleSuccess, handleError } = useNotificationMessages();
  const { data: userData = [] } = useAdminUsersQuery();
  const { data = {} } = usePurchaseOrderByIdQuery({ id });
  const { assignments = {} } = data;

  const [updatePurchaseOrderManagement, { isLoading: isLoadingUpdate }] = useUpdatePurchaseOrderManagementMutation();

  const initialAssignment: Record<string, string> = useMemo(
    () => ({
      postSalesManagerId: assignments?.postSalesManager?.id,
      postSalesManagerAssistantId: assignments?.postSalesManagerAssistant?.id,
    }),
    [assignments]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    onCancelModal();
  }, [form, onCancelModal]);

  const onFinish = useCallback(async () => {
    try {
      const payload = form.getFieldsValue() || {};
      const isFormChanged = Object.keys(payload).some((key) => initialAssignment[key] !== payload[key]);

      if (!isFormChanged) {
        onCancel();
        return;
      }
      payload.id = id as string;
      const { data, error }: Record<string, any> = await updatePurchaseOrderManagement(payload);
      if (data?.success) {
        handleSuccess('Purchase Order assignments were successfully updated.');
        onCancel();
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  }, [updatePurchaseOrderManagement, handleSuccess, handleError, id, onCancel, form, initialAssignment]);

  useEffect(() => {
    if (open && initialAssignment) {
      form.setFieldsValue(initialAssignment);
    }
  }, [form, open, initialAssignment]);

  const handleManagerChange = useCallback(
    (value: string, key: string) => {
      if (value === form.getFieldValue([key])) {
        form.setFieldsValue({ [key]: '' });
      }
    },
    [form]
  );

  return {
    form,
    isLoadingUpdate,
    onFinish,
    onCancel,
    userData,
    handleManagerChange,
  };
};
